import { computed, type Ref } from 'vue'

export interface MetricOption {
  label: string
  value: string
  category: string
}

export interface ScopeOption {
  label: string
  value: string
}

export const METRIC_OPTIONS: MetricOption[] = [
  // Performance
  { label: 'ROI', value: 'performance_metric:roi', category: 'Performance' },
  { label: 'MOIC', value: 'performance_metric:moic', category: 'Performance' },
  { label: 'TVPI', value: 'performance_metric:tvpi', category: 'Performance' },
  { label: 'RVPI', value: 'performance_metric:rvpi', category: 'Performance' },
  { label: 'DPI', value: 'performance_metric:dpi', category: 'Performance' },

  // Industry Analysis
  { label: 'ROI by Industry', value: 'roi_by_industry', category: 'Industry Analysis' },
  { label: 'Avg Investment per Company', value: 'avg_investment_per_company', category: 'Industry Analysis' },

  // Commitments
  { label: 'Total Commitments', value: 'commitment:total', category: 'Commitments' },
  { label: 'Average Commitment', value: 'commitment:average', category: 'Commitments' },
  { label: 'Total SPV Commitments', value: 'total_spv_commitments', category: 'Commitments' },

  // Counts
  { label: 'Entity Counts', value: 'entity_count', category: 'Counts' },
  { label: 'Investor Count', value: 'investor_count', category: 'Counts' },

  // Cash
  { label: 'Cash Position', value: 'cash_position', category: 'Cash' },
  { label: 'Cash Position Snapshot', value: 'investor_set_cash_position_snapshot', category: 'Cash' },

  // Investment Activity
  { label: 'New Investments', value: 'investment_activity:new', category: 'Investment Activity' },
  { label: 'Follow-up Investments', value: 'investment_activity:follow_up', category: 'Investment Activity' },

  // Investment Value
  { label: 'Average Initial Value', value: 'investment_value:initial_value', category: 'Investment Value' },
  { label: 'Average Current Value', value: 'investment_value:current_value', category: 'Investment Value' },
  { label: 'Average Investment Size', value: 'investment_value:investment_size', category: 'Investment Value' },

  // Fund Terms
  { label: 'Carried Interest', value: 'fund_terms:carried_interest', category: 'Fund Terms' },
  { label: 'Management Fee', value: 'fund_terms:management_fee', category: 'Fund Terms' },
  { label: 'Preferred Return', value: 'fund_terms:preferred_return', category: 'Fund Terms' },

  // Applications
  { label: 'Funding Applications Received', value: 'funding_application:received', category: 'Applications' },
  { label: 'Funding Applications Funded', value: 'funding_application:funded', category: 'Applications' },

  // Investment Type
  { label: 'Equity %', value: 'investment_type:equity', category: 'Investment Type' },
  { label: 'Note %', value: 'investment_type:note', category: 'Investment Type' },

  // Exports
  { label: 'SPV Export (2023-2025)', value: 'spv_export', category: 'Exports' },
]

const SCOPE_OPTIONS: ScopeOption[] = [
  { label: 'Global', value: 'global' },
  { label: 'Global (Funds)', value: 'global_fund' },
  { label: 'Global (SPVs)', value: 'global_spv' },
  { label: 'Group', value: 'group' },
  { label: 'Investor Set', value: 'investor_set' },
]

// Which scopes each query supports
const QUERY_SCOPES: Record<string, string[]> = {
  performance_metric: ['global', 'global_fund', 'global_spv', 'group', 'investor_set'],
  roi_by_industry: ['global'],
  avg_investment_per_company: ['global'],
  total_spv_commitments: ['global'],
  investor_count: ['global', 'group', 'investor_set'],
  commitment: ['global', 'global_fund', 'global_spv', 'group', 'investor_set'],
  cash_position: ['global', 'group', 'investor_set'],
  investor_set_cash_position_snapshot: ['global', 'global_fund', 'global_spv'],
  entity_count: ['global', 'group'],
  investment_activity: ['global', 'group', 'investor_set'],
  investment_value: ['global', 'group', 'investor_set'],
  fund_terms: ['global', 'group', 'investor_set'],
  funding_application: ['global', 'group'],
  investment_type: ['group'],
  spv_export: ['global'],
}

// Which queries support year filtering
const YEAR_QUERIES = ['roi_by_industry', 'avg_investment_per_company', 'total_spv_commitments']

// Which queries support multi-year (user selects one or more years)
const MULTI_YEAR_QUERIES = ['investor_set_cash_position_snapshot']

export function useMetricOptions(selectedMetric: Ref<string | null>) {
  const categories = computed(() => {
    const grouped: Record<string, MetricOption[]> = {}
    for (const option of METRIC_OPTIONS) {
      if (!grouped[option.category]) grouped[option.category] = []
      grouped[option.category].push(option)
    }
    return grouped
  })

  const queryName = computed(() => {
    if (!selectedMetric.value) return null
    return selectedMetric.value.split(':')[0]
  })

  const availableScopes = computed(() => {
    if (!queryName.value) return []
    const allowed = QUERY_SCOPES[queryName.value] || []
    return SCOPE_OPTIONS.filter((s) => allowed.includes(s.value))
  })

  const needsGroupId = computed(() => {
    return (scope: string) => scope === 'group'
  })

  const needsInvestorSetId = computed(() => {
    return (scope: string) => scope === 'investor_set'
  })

  const supportsYear = computed(() => {
    return queryName.value ? YEAR_QUERIES.includes(queryName.value) : false
  })

  const supportsMultiYear = computed(() => {
    return queryName.value ? MULTI_YEAR_QUERIES.includes(queryName.value) : false
  })

  const yearOptions = computed(() => {
    const currentYear = new Date().getFullYear()
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]
  })

  // Parse the metric value into query params
  function buildParams(
    metric: string,
    scope: string | null,
    year: number | null,
    groupId: number | null,
    investorSetId: number | null,
    years: number[] | null = null,
  ) {
    const parts = metric.split(':')
    const query = parts[0]
    const subParam = parts[1] || null

    const params: Record<string, any> = { query }

    // Map sub-params to the right query parameter
    if (query === 'performance_metric' && subParam) params.metric = subParam
    if (query === 'commitment' && subParam) params.aggregate = subParam
    if (query === 'investment_activity' && subParam) params.activity_type = subParam
    if (query === 'investment_value' && subParam) params.measure = subParam
    if (query === 'fund_terms' && subParam) params.term = subParam
    if (query === 'funding_application' && subParam) params.measure = subParam
    if (query === 'investment_type' && subParam) params.investment_type = subParam

    // Scope → entity_type for global_fund / global_spv
    if (scope === 'global_fund') params.entity_type = 'fund'
    else if (scope === 'global_spv') params.entity_type = 'spv'

    if (scope === 'group' && groupId) params.group_id = groupId
    if (scope === 'investor_set' && investorSetId) params.investor_set_id = investorSetId

    if (year) params.year = year
    if (years && years.length) params.years = years

    return params
  }

  return {
    categories,
    queryName,
    availableScopes,
    needsGroupId,
    needsInvestorSetId,
    supportsYear,
    supportsMultiYear,
    yearOptions,
    buildParams,
  }
}
